/**
 * ComplianceRate — Shows % of assigned workouts completed by a client.
 *
 * Reads program_assignments and cross-references with completed workouts.
 * Falls back to a simple "sessions per week" metric if no programs assigned.
 *
 * Zero effect on scoring/matchmaking (doctrine compliant).
 */

import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { supabase } from '@/lib/supabase';
import { CheckCircle, Settings } from 'lucide-react-native';

const TARGET_KEY = (clientId: string) => `compliance_target_${clientId}`;

export async function getComplianceTarget(clientId: string): Promise<number> {
  try {
    const val = await AsyncStorage.getItem(TARGET_KEY(clientId));
    return val ? Number(val) : 4;
  } catch { return 4; }
}

export async function setComplianceTarget(clientId: string, target: number): Promise<void> {
  await AsyncStorage.setItem(TARGET_KEY(clientId), String(target));
}

interface Props {
  clientId: string;
  /** If true, show target selector UI */
  editable?: boolean;
}

export default function ComplianceRate({ clientId, editable = false }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [compliance, setCompliance] = useState<number | null>(null);
  const [detail, setDetail] = useState('');
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(4);
  const [showTargetPicker, setShowTargetPicker] = useState(false);

  const TARGET_OPTIONS = [2, 3, 4, 5, 6];

  useEffect(() => {
    loadCompliance();
  }, [clientId, target]);

  useEffect(() => {
    getComplianceTarget(clientId).then(setTarget);
  }, [clientId]);

  const handleTargetChange = async (newTarget: number) => {
    setTarget(newTarget);
    await setComplianceTarget(clientId, newTarget);
    setShowTargetPicker(false);
  };

  const loadCompliance = async () => {
    setLoading(true);
    try {
      // Check for program assignments
      const { data: assignments } = await supabase
        .from('program_assignments')
        .select('id, program_id, status, created_at')
        .eq('client_id', clientId)
        .eq('status', 'active');

      if (assignments && assignments.length > 0) {
        // Count completed sessions in the last 30 days
        const d30 = new Date();
        d30.setDate(d30.getDate() - 30);
        const since = d30.toISOString();

        const { count: completedCount } = await supabase
          .from('workouts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', clientId)
          .gte('performed_at', since);

        // Expected: configurable sessions/week × 4.3 weeks
        const expectedSessions = Math.round(target * 4.3);
        const actual = completedCount ?? 0;
        const rate = Math.min(100, Math.round((actual / expectedSessions) * 100));

        setCompliance(rate);
        setDetail(`${actual} of ~${expectedSessions} expected sessions (30d, ${target}x/week)`);
      } else {
        // No assignments — use workout frequency
        const d30 = new Date();
        d30.setDate(d30.getDate() - 30);
        const since = d30.toISOString();

        const { count: workoutCount } = await supabase
          .from('workouts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', clientId)
          .gte('performed_at', since);

        const count = workoutCount ?? 0;
        const sessionsPerWeek = Math.round((count / 4.3) * 10) / 10;

        // No assigned program, so show frequency instead
        setCompliance(null);
        setDetail(
          count > 0
            ? `${count} sessions in 30 days (~${sessionsPerWeek}/week, target: ${target}x)`
            : `No workouts in the last 30 days (target: ${target}x/week)`,
        );
      }
    } catch (err) {
      console.warn('[ComplianceRate] load error', err);
      setDetail('Could not load compliance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ActivityIndicator color={c.accent} style={{ marginVertical: 16 }} />;
  }

  const getColor = () => {
    if (compliance === null) return c.textSecondary;
    if (compliance >= 80) return '#22c55e';
    if (compliance >= 50) return '#eab308';
    return '#ef4444';
  };

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.headerRow}>
        <CheckCircle size={16} color={c.accent} />
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
          COMPLIANCE
        </Text>
        {editable && (
          <TouchableOpacity
            onPress={() => setShowTargetPicker(!showTargetPicker)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Settings size={14} color={c.textSecondary} />
          </TouchableOpacity>
        )}
      </View>

      {/* Target picker */}
      {showTargetPicker && (
        <View style={styles.targetRow}>
          <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600', marginRight: 8 }}>Target:</Text>
          {TARGET_OPTIONS.map((opt) => (
            <TouchableOpacity
              key={opt}
              style={[styles.targetBtn, { backgroundColor: target === opt ? c.accent : c.surface, borderColor: target === opt ? c.accent : c.border }]}
              onPress={() => handleTargetChange(opt)}
            >
              <Text style={{ color: target === opt ? '#fff' : c.textSecondary, fontSize: 12, fontWeight: '700' }}>
                {opt}x
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {compliance !== null ? (
        <View style={styles.scoreRow}>
          <Text style={[styles.scoreValue, { color: getColor(), fontFamily: TYPO.heading.bold }]}>
            {compliance}%
          </Text>
          <View style={[styles.progressTrack, { backgroundColor: c.border }]}>
            <View
              style={[
                styles.progressFill,
                {
                  backgroundColor: getColor(),
                  width: `${compliance}%`,
                },
              ]}
            />
          </View>
        </View>
      ) : null}

      <Text style={[styles.detail, { color: c.textSecondary }]}>
        {detail}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  title: {
    flex: 1,
    fontSize: 13,
    letterSpacing: 1,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  scoreValue: {
    fontSize: 28,
    width: 70,
  },
  progressTrack: {
    flex: 1,
    height: 10,
    borderRadius: 5,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 5,
  },
  detail: {
    fontSize: 12,
    fontWeight: '500',
  },
  targetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    flexWrap: 'wrap',
    gap: 4,
  },
  targetBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
    borderWidth: 1,
  },
});
